import { create } from 'zustand';
import { CollectionInterface, Permissions, ProfileInterface } from "ts-interfaces";
import { useLoginState} from '../studioxr/auth/LoginState';
import { 
  getOwnedCollections,
  getReadOnlyCollections,
  getWritableSharedCollections,
  getProjectReadCollections,
  getProjectWriteCollections,
  getUserProjetsCollections,
  getCollections,

} from '../v2/apis/collectionsSchemeApi';

import { curatedTemplateUserID } from '../libs/firebase';

// ✅ PAGINATION CONSTANTS
const COLLECTIONS_PAGE_SIZE = 20;

// ✅ COLLECTION TYPE CONSTANTS - Single source of truth
export const COLLECTION_TYPES = {
  INDIVIDUAL: 'individual',
  SHARED: 'shared',
  OWNED_PROJECT: 'ownedProject',
  SHARED_PROJECT: 'sharedProject'
} as const;

// ✅ Type derived from constants - ensures consistency
export type CollectionType = typeof COLLECTION_TYPES[keyof typeof COLLECTION_TYPES];

interface CollectionDetailed extends CollectionInterface {
  isEditable: boolean;
  collectionType: CollectionType; // ✅ Now uses the derived type
}

interface CollectionsState {
  // ✅ SINGLE SOURCE OF TRUTH
  collections: CollectionDetailed[];
  curatedCollections: CollectionInterface[];
  
  // ✅ CONSISTENT SCOPED LOADING STATES  
  isLoadingUserCollections: boolean;        
  isLoadingCuratedCollections: boolean;     
  initialUserCollectionsFetchDone: boolean; 
  hasMoreUserCollections: boolean;          
  hasMoreCuratedCollections: boolean;      
  
  // ✅ CONTEXT
  currentUserId: string | null;
  
  // ✅ PRO ACCESS GETTER
  getTeamsProAccess: () => {
    isProUser: boolean;
    isTeamsProUser: boolean;
  };
  
  // ✅ CORE ACTIONS
  fetchCollections: (userId: string, permissions: Permissions | null) => Promise<{
    collections: CollectionInterface[];
    getNext: () => Promise<void>;
    isLoading: boolean;
  }>;
  getNext: () => Promise<void>;
  refreshCollections: () => Promise<void>;
  
  // ✅ CURATED COLLECTIONS METHODS
  fetchCuratedCollections: () => Promise<void>;
  getMoreCuratedCollections: () => Promise<void>;
  
  // ✅ COLLECTION OPERATIONS
  updateCollection: (updatedCollection: CollectionInterface) => void;
  removeCollection: (collectionId: string) => void;
  syncCollectionAssociations: (collectionsToAdd: string[], collectionsToRemove: string[], projectId: string) => void;
  saveCollection: (collection: CollectionInterface) => void;

  // ✅ COMPUTED GETTERS (no complex filtering - that's done in components)
  getOwnedCollections: () => CollectionDetailed[];
  getSharedCollections: () => CollectionDetailed[];
  getEditableCollections: () => CollectionDetailed[];
  getProjectCollections: () => CollectionDetailed[];
  
  // ✅ HELPER FOR PROJECT FILTER
  getProjectsWithCollectionCounts: () => Map<string, number>;
}

export const useCollections = create<CollectionsState>((set, get) => {
  
  const addOwnedMetadata = (collection: CollectionInterface): CollectionDetailed => {
    return { ...collection, isEditable: true, collectionType: COLLECTION_TYPES.INDIVIDUAL };
  };

  const addReadOnlyMetadata = (collection: CollectionInterface): CollectionDetailed => {
    return { ...collection, isEditable: false, collectionType: COLLECTION_TYPES.SHARED };
  };

  const addWriteableMetadata = (collection: CollectionInterface): CollectionDetailed => {
    return { ...collection, isEditable: true, collectionType: COLLECTION_TYPES.SHARED };
  };

  const addProjectReadMetadata = (collection: CollectionInterface): CollectionDetailed => {
    return { ...collection, isEditable: false, collectionType: COLLECTION_TYPES.SHARED_PROJECT };
  };

  const addProjectWriteMetadata = (collection: CollectionInterface): CollectionDetailed => {
    return { ...collection, isEditable: true, collectionType: COLLECTION_TYPES.SHARED_PROJECT };
  };

  const addUserProjectsMetadata = (collection: CollectionInterface): CollectionDetailed => {
    return { ...collection, isEditable: true, collectionType: COLLECTION_TYPES.OWNED_PROJECT };
  };

  const processCollections = (collectionsWithMetadata: CollectionDetailed[], userId: string): CollectionDetailed[] => {
    // Helper function to remove duplicates from individual collections
    const removeDuplicatesFromIndividual = (
      individual: CollectionDetailed[], 
      ...otherArrays: CollectionDetailed[][]
    ): CollectionDetailed[] => {
      const otherCollectionIds = new Set(
        otherArrays.flat().map(collection => collection.id)
      );
      return individual.filter(collection => !otherCollectionIds.has(collection.id));
    };

    // Organize collections by type
    const myIndividualCollections = collectionsWithMetadata.filter(c => 
      c.collectionType === COLLECTION_TYPES.INDIVIDUAL && c.uid === userId && !c.associatedProjectId
    );
    const sharedIndividualCollections = collectionsWithMetadata.filter(c => 
      c.collectionType === COLLECTION_TYPES.SHARED && c.uid !== userId
    );
    const myProjectCollections = collectionsWithMetadata.filter(c => 
      c.collectionType === COLLECTION_TYPES.OWNED_PROJECT && c.uid === userId
    );
    const sharedProjectCollections = collectionsWithMetadata.filter(c => 
      c.collectionType === COLLECTION_TYPES.SHARED_PROJECT
    );

    // Apply Pro filtering and deduplication
    const { isProUser, isTeamsProUser } = get().getTeamsProAccess();
    
    // Teams PRO users see all collections but remove duplicates from individual
    if (isTeamsProUser) {
      const deduplicatedIndividual = removeDuplicatesFromIndividual(
        myIndividualCollections,
        myProjectCollections,
        sharedProjectCollections
      );
      
      return [
        ...deduplicatedIndividual,
        ...sharedIndividualCollections,
        ...myProjectCollections,
        ...sharedProjectCollections
      ];
    }
    
    // PRO users see their individual collections + their owned project collections
    // Remove duplicates from individual that exist in project collections
    if (isProUser && !isTeamsProUser) {
      const ownedProjectCollections = myProjectCollections.filter(c => c.uid === userId);
      const deduplicatedIndividual = removeDuplicatesFromIndividual(
        myIndividualCollections,
        ownedProjectCollections
      );

      return [
        ...deduplicatedIndividual,
        ...ownedProjectCollections
      ];
    }
    
    // Non-PRO users see only individual collections (no project collections)
    return myIndividualCollections.filter(c => !c.associatedProjectId);
  };

  const removeDuplicates = (collections: CollectionDetailed[]) => {
    const seen = new Set();
    return collections.filter(c => {
      if (seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });
  };

  // ✅ SIMPLIFIED FETCH COLLECTIONS FUNCTION
  const fetchCollections = async (userId: string, permissions: Permissions | null) => {
    if (!userId) {
      return { collections: [], getNext: get().getNext, isLoading: false };
    }

    const state = get();
    set({ isLoadingUserCollections: true, currentUserId: userId });
    
    try {
      if (!state.initialUserCollectionsFetchDone || userId !== state.currentUserId) {
        // Fetch all collection types in parallel
        const [owned, readOnly, writableShared, projectRead, projectWrite, userProjects] = await Promise.all([
          getOwnedCollections(userId, [], COLLECTIONS_PAGE_SIZE),
          getReadOnlyCollections(permissions, [], COLLECTIONS_PAGE_SIZE),
          getWritableSharedCollections(permissions, [], COLLECTIONS_PAGE_SIZE),
          getProjectReadCollections(permissions, [], COLLECTIONS_PAGE_SIZE),
          getProjectWriteCollections(permissions, [], COLLECTIONS_PAGE_SIZE),
          getUserProjetsCollections(userId, [], COLLECTIONS_PAGE_SIZE)
        ]);

        // Apply metadata to each collection type
        const ownedWithMetadata = owned.map(addOwnedMetadata);
        const readOnlyWithMetadata = readOnly.map(addReadOnlyMetadata);
        const writableSharedWithMetadata = writableShared.map(addWriteableMetadata);
        const projectReadWithMetadata = projectRead.map(addProjectReadMetadata);
        const projectWriteWithMetadata = projectWrite.map(addProjectWriteMetadata);
        const userProjectsWithMetadata = userProjects.map(addUserProjectsMetadata);
        
        // Combine all collections with metadata
        const allCollectionsWithMetadata = [
          ...ownedWithMetadata, 
          ...readOnlyWithMetadata, 
          ...writableSharedWithMetadata, 
          ...projectReadWithMetadata, 
          ...projectWriteWithMetadata, 
          ...userProjectsWithMetadata
        ];
        
        // Apply pro filtering
        const processedCollections = processCollections(allCollectionsWithMetadata, userId);
        const uniqueCollections = removeDuplicates(processedCollections);

        set({
          collections: uniqueCollections,
          isLoadingUserCollections: false,
          initialUserCollectionsFetchDone: true,
          hasMoreUserCollections: uniqueCollections.length >= COLLECTIONS_PAGE_SIZE
        });
      } else {
        // ✅ FIX: Data already loaded, immediately set loading to false
        set({ isLoadingUserCollections: false });
      }
      
    } catch (error) {
      console.error("Failed to initialize collections:", error);
      set({ isLoadingUserCollections: false });
    }
    
    return {
      collections: get().collections,
      getNext: get().getNext,
      isLoading: get().isLoadingUserCollections
    };
  };



  return {
    // ✅ STATE
    collections: [],
    curatedCollections: [],
    isLoadingUserCollections: false,        // ✅ Updated to new consistent naming
    isLoadingCuratedCollections: false,     // ✅ Updated to new consistent naming
    initialUserCollectionsFetchDone: false, // ✅ Updated to new consistent naming
    hasMoreUserCollections: true,
    hasMoreCuratedCollections: true,  // ✅ Initially assume there might be curated collections to load
    currentUserId: null,



    // 🔧 PRO ACCESS GETTER
    getTeamsProAccess: () => {
      const loginState = useLoginState.getState();
      return {
        isProUser: loginState.isProUser,
        isTeamsProUser: loginState.isTeamsProUser,
      };
    },

    // 🔧 FETCH METHODS
    fetchCollections,

    // ✅ SIMPLIFIED getNext
    getNext: async () => {
      const { currentUserId, collections } = get();
      if (!currentUserId) return;

      set({ isLoadingUserCollections: true });
      
      try {
        const loginState = useLoginState.getState();
        const permissions = loginState.permissions;
        
        // Fetch more collections using last collection as cursor
        const lastCollection = collections[collections.length - 1];
        const [moreOwned, moreReadOnly, moreWritableShared, moreProjectRead, moreProjectWrite] = await Promise.all([
          getOwnedCollections(currentUserId, [lastCollection], COLLECTIONS_PAGE_SIZE),
          getReadOnlyCollections(permissions, [lastCollection], COLLECTIONS_PAGE_SIZE),
          getWritableSharedCollections(permissions, [lastCollection], COLLECTIONS_PAGE_SIZE),
          getProjectReadCollections(permissions, [lastCollection], COLLECTIONS_PAGE_SIZE),
          getProjectWriteCollections(permissions, [lastCollection], COLLECTIONS_PAGE_SIZE)
        ]);
        
        // Apply metadata to each collection type
        const moreOwnedWithMetadata = moreOwned.map(addOwnedMetadata);
        const moreReadOnlyWithMetadata = moreReadOnly.map(addReadOnlyMetadata);
        const moreWritableSharedWithMetadata = moreWritableShared.map(addWriteableMetadata);
        const moreProjectReadWithMetadata = moreProjectRead.map(addProjectReadMetadata);
        const moreProjectWriteWithMetadata = moreProjectWrite.map(addProjectWriteMetadata);
        
        const newCollectionsWithMetadata = [
          ...moreOwnedWithMetadata, 
          ...moreReadOnlyWithMetadata, 
          ...moreWritableSharedWithMetadata, 
          ...moreProjectReadWithMetadata, 
          ...moreProjectWriteWithMetadata
        ];
        const newProcessedCollections = processCollections(newCollectionsWithMetadata, currentUserId);
        const updatedCollections = removeDuplicates([...collections, ...newProcessedCollections]);
        
        set({
          collections: updatedCollections,
          isLoadingUserCollections: false,
          hasMoreUserCollections: newCollectionsWithMetadata.length > 0
        });
        
      } catch (error) {
        console.error("Failed to fetch more collections:", error);
        set({ isLoadingUserCollections: false });
      }
    },

    refreshCollections: async () => {
      const { currentUserId } = get();
      if (!currentUserId) return; 
      
      set({ 
        collections: [], 
        curatedCollections: [], 
        initialUserCollectionsFetchDone: false 
      });
      const loginState = useLoginState.getState();
      await get().fetchCollections(currentUserId, loginState.permissions);
    },

    // 🔧 CURATED COLLECTIONS METHODS
    fetchCuratedCollections: async () => {
      try {
        set({ isLoadingCuratedCollections: true });
        const curatedCollections = await getCollections(curatedTemplateUserID, [], COLLECTIONS_PAGE_SIZE) as CollectionInterface[];
        set({ 
          curatedCollections,
          hasMoreCuratedCollections: curatedCollections.length >= COLLECTIONS_PAGE_SIZE,  // ✅ Positive logic: >= COLLECTIONS_PAGE_SIZE means might have more
          isLoadingCuratedCollections: false 
        });
      } catch (error) {
        console.error('Error fetching curated collections:', error);
        set({ isLoadingCuratedCollections: false });
      }
    },

    getMoreCuratedCollections: async () => {
      const { curatedCollections, hasMoreCuratedCollections } = get();
      if (!hasMoreCuratedCollections) return;  // ✅ If no more available, don't fetch
      
      try {
        set({ isLoadingCuratedCollections: true });
        const newCuratedCollections = await getCollections(curatedTemplateUserID, curatedCollections, COLLECTIONS_PAGE_SIZE) as CollectionInterface[];
        const mergedCollections = [...curatedCollections, ...newCuratedCollections];
        
        set({
          curatedCollections: mergedCollections,
          hasMoreCuratedCollections: newCuratedCollections.length >= COLLECTIONS_PAGE_SIZE,  // ✅ Positive logic: >= COLLECTIONS_PAGE_SIZE means might have more
          isLoadingCuratedCollections: false
        });
      } catch (error) {
        console.error('Error fetching more curated collections:', error);
        set({ isLoadingCuratedCollections: false });
      }
    },

    // ✅ SIMPLIFIED COLLECTION OPERATIONS
    updateCollection: (updatedCollection: CollectionInterface) => {
      const { currentUserId } = get();
      if (!currentUserId) return;
      
      set(state => {
        const existingCollection = state.collections.find(c => c.id === updatedCollection.id);
        if (!existingCollection) return state; // Collection not found, no update needed
        
        // Re-evaluate collection type based on updated data instead of preserving original type
        let processedCollection: CollectionDetailed;
        
        // For owned collections, determine type based on project association
        if (updatedCollection.uid === currentUserId) {
          if (updatedCollection.associatedProjectId) {
            // Owned collection with project association -> OWNED_PROJECT
            processedCollection = addUserProjectsMetadata(updatedCollection);
          } else {
            // Owned collection without project association -> INDIVIDUAL
            processedCollection = addOwnedMetadata(updatedCollection);
          }
        } else {
          // For shared collections, preserve the existing shared type logic
          switch (existingCollection.collectionType) {
            case COLLECTION_TYPES.SHARED:
              processedCollection = existingCollection.isEditable 
                ? addWriteableMetadata(updatedCollection)
                : addReadOnlyMetadata(updatedCollection);
              break;
            case COLLECTION_TYPES.SHARED_PROJECT:
              processedCollection = existingCollection.isEditable
                ? addProjectWriteMetadata(updatedCollection)
                : addProjectReadMetadata(updatedCollection);
              break;
            default:
              // Fallback for shared collections
              processedCollection = addReadOnlyMetadata(updatedCollection);
          }
        }
        
        const newCollections = state.collections
          .map(c => c.id === updatedCollection.id ? processedCollection : c)
          .sort((a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime());
        
        return {
          collections: newCollections
        };
      });
    },

    removeCollection: (collectionId: string) => {
      set(state => {
        const newCollections = state.collections.filter(c => c.id !== collectionId);
        return {
          collections: newCollections
        };
      });
    },

    // ✅ UNIFIED SAVE METHOD - handles both add and update intelligently
    saveCollection: (collection: CollectionInterface) => {
      const { collections } = get();
      const existingCollection = collections.find(c => c.id === collection.id);
      
      if (existingCollection) {
        // Collection exists - update it while preserving metadata
        get().updateCollection(collection);
      } else {
        // New collection - add it with owned metadata
        set(state => {
          const processedCollection = addOwnedMetadata(collection);
          return {
            collections: [processedCollection, ...state.collections].sort((a, b) => 
              new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime()
            )
          };
        });
      }
    },

    syncCollectionAssociations: (collectionsToAdd: string[], collectionsToRemove: string[], projectId: string) => {
      set(state => {
        const newCollections = state.collections.map(collection => {
          if (collectionsToAdd.includes(collection.id)) {
            return { ...collection, associatedProjectId: projectId };
          }
          if (collectionsToRemove.includes(collection.id)) {
            return { ...collection, associatedProjectId: undefined };
          }
          return collection;
        });
        
        return {
          collections: newCollections
        };
      });
    },

    // 🔧 COMPUTED GETTERS (Pure functions - no state storage)
    getOwnedCollections: () => {
      const { collections, currentUserId } = get();
      return collections.filter(c => 
        (c.collectionType === COLLECTION_TYPES.INDIVIDUAL && c.uid === currentUserId) || 
        (c.collectionType === COLLECTION_TYPES.OWNED_PROJECT && c.uid === currentUserId) ||
        (c.collectionType === COLLECTION_TYPES.SHARED_PROJECT && c.uid === currentUserId)
      );
    },
    getSharedCollections: () => {
      const { collections, currentUserId } = get();
      return collections.filter(c => 
        (c.collectionType === COLLECTION_TYPES.SHARED && c.uid !== currentUserId) || 
        c.collectionType === COLLECTION_TYPES.SHARED_PROJECT && c.uid !== currentUserId ||
        (c.collectionType === COLLECTION_TYPES.OWNED_PROJECT && c.uid !== currentUserId)
      );
    },
    getEditableCollections: () => {
      const { collections } = get();
      return collections.filter(c => c.isEditable);
    },
    getProjectCollections: () => {
      const { collections, currentUserId } = get();
      return collections.filter(c => c.collectionType === COLLECTION_TYPES.OWNED_PROJECT && c.uid === currentUserId);
    },
    

    // ✅ HELPER FOR PROJECT FILTER
    getProjectsWithCollectionCounts: () => {
      const { collections } = get();
      const projectCounts = new Map<string, number>();
      
      collections.forEach(collection => {
        if (collection.associatedProjectId) {
          const currentCount = projectCounts.get(collection.associatedProjectId) || 0;
          projectCounts.set(collection.associatedProjectId, currentCount + 1);
        }
      });
      
      return projectCounts;
    }
  };
}); 